import React from 'react';
import {
  Container,
  SpaceBetween,
  Box,
  Button
} from "@amzn/awsui-components-react";

/**
 * ProcessSidebar Component
 * Displays a collapsible sidebar with process lists for FlexiCo, Frisco, and Logico
 */
const ProcessSidebar = ({
  isOpen,
  onToggle,
  activeSubTabId,
  flexicoTemplates,
  subProcesses,
  logicoProcesses,
  selectedSidebarItemId,
  onProcessSelect,
  canEdit,
  canAdd,
  onAddNew,
  onProgrammaticAdd,
  id
}) => {
  
  // Render FlexiCo templates list
  const renderFlexicoList = () => {
    if (activeSubTabId !== 'run') return null;
    
    if (flexicoTemplates && flexicoTemplates.length > 0) {
      return flexicoTemplates.map((template) => {
        const processUrl = `/App/${id}/flexico/${template.flex_id}/upload`;
        
        return (
          <Box key={template.flex_id} display="flex" alignItems="center" justifyContent="space-between">
            <a 
              href={processUrl}
              onClick={(e) => {
                e.preventDefault();
                onProcessSelect('flexico', template.flex_id, 'execute');
              }}
              className={selectedSidebarItemId === template.flex_id ? "active-link" : "inactive-link"}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="inline-link"
                className={selectedSidebarItemId === template.flex_id ? "active-link" : "inactive-link"}
              >
                {template.label}
              </Button>
            </a>
          </Box>
        );
      });
    }
    
    return (
      <Box padding="s">
        <Box variant="small" color="text-body-secondary">No automation processes available</Box>
      </Box>
    );
  };

  // Render Logico processes list
  const renderLogicoList = () => {
    if (activeSubTabId !== 'categorize') return null;
    
    if (logicoProcesses && logicoProcesses.length > 0) {
      return logicoProcesses.map((process) => {
        const processUrl = `/App/${id}/LogicoProcess/${process.logico_process_id}/upload`;
        
        return (
          <Box key={process.logico_process_id} display="flex" alignItems="center" justifyContent="space-between">
            <a 
              href={processUrl}
              onClick={(e) => {
                e.preventDefault();
                onProcessSelect('logico', process.logico_process_id, 'upload');
              }}
              className={selectedSidebarItemId === process.logico_process_id ? "active-link" : "inactive-link"}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="inline-link"
                className={selectedSidebarItemId === process.logico_process_id ? "active-link" : "inactive-link"}
              >
                {process.display_label}
              </Button>
            </a>
          </Box>
        );
      });
    }
    
    return (
      <Box padding="s">
        <Box variant="small" color="text-body-secondary">No categorization processes available</Box>
      </Box>
    );
  };

  // Render Frisco processes list
  const renderFriscoList = () => {
    if (activeSubTabId !== 'upload') return null;
    
    if (subProcesses && subProcesses.length > 0) {
      return subProcesses.map((process) => {
        const processUrl = `/App/${id}/process/${process.process_id}/upload`;
        
        return (
          <Box key={process.process_id} display="flex" alignItems="center" justifyContent="space-between">
            <a 
              href={processUrl}
              onClick={(e) => {
                e.preventDefault();
                onProcessSelect('frisco', process.process_id, 'upload');
              }}
              className={selectedSidebarItemId === process.process_id ? "active-link" : "inactive-link"}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant="inline-link"
                className={selectedSidebarItemId === process.process_id ? "active-link" : "inactive-link"}
              >
                {process.label}
              </Button>
            </a>
          </Box>
        );
      });
    }
    
    return (
      <Box padding="s">
        <Box variant="small" color="text-body-secondary">No upload processes available</Box>
      </Box>
    );
  };

  // If sidebar is closed, show only the toggle button
  if (!isOpen) {
    return (
      <Box float="left">
        <Button
          variant="icon"
          iconName="menu"
          onClick={onToggle}
          ariaLabel="Show processes"
        />
      </Box>
    );
  }

  // Render full sidebar when open
  return (
    <Container>
      <SpaceBetween size="s">
        <Box float="right">
          <Button
            variant="icon"
            iconName="angle-left-double"
            onClick={onToggle}
            ariaLabel="Hide processes"
          />
        </Box>
        
        {renderFlexicoList()}
        {renderLogicoList()}
        {renderFriscoList()}
        
        {canEdit && (
          <Box>
            <Button
              variant="link"
              iconName="add-plus"
              onClick={onAddNew}
            >
              Add New
            </Button>
          </Box>
        )}
        {/* FlexiCo only: create templates from a JSON file instead of the form. Gated on add
            permission, which is what the create action inside it requires. */}
        {canAdd && activeSubTabId === 'run' && onProgrammaticAdd && (
          <Box>
            <Button
              variant="link"
              iconName="upload"
              onClick={onProgrammaticAdd}
            >
              Programmatic add
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default ProcessSidebar;
